import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { MapPin, Phone, Mail, ShieldCheck, ChevronDown, Menu, X, Facebook, Twitter, Linkedin } from "lucide-react";
import { COMPANY, NAV_LINKS, SERVICES } from "../data/mock";
import { useQuote } from "./QuoteModal";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const { openQuote } = useQuote();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <>
      {/* Top bar */}
      <div className="hidden bg-[#006FB9] text-[#D1E7F3] lg:block">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-2 text-[13px]">
          <div className="flex items-center gap-6">
            <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />{COMPANY.address}</span>
            <a href={`mailto:${COMPANY.email}`} className="flex items-center gap-1.5 transition-colors hover:text-white"><Mail className="h-3.5 w-3.5" />{COMPANY.email}</a>
            <a href={COMPANY.phoneHref} className="flex items-center gap-1.5 transition-colors hover:text-white"><Phone className="h-3.5 w-3.5" />{COMPANY.phone}</a>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-[#91C4DE]">{COMPANY.hours}</span>
            <span className="h-3.5 w-px bg-white/20" />
            <a href="https://facebook.com" target="_blank" rel="noreferrer" aria-label="Facebook" className="transition-colors hover:text-white"><Facebook className="h-3.5 w-3.5" /></a>
            <a href="https://twitter.com" target="_blank" rel="noreferrer" aria-label="Twitter" className="transition-colors hover:text-white"><Twitter className="h-3.5 w-3.5" /></a>
            <a href="https://linkedin.com" target="_blank" rel="noreferrer" aria-label="LinkedIn" className="transition-colors hover:text-white"><Linkedin className="h-3.5 w-3.5" /></a>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <header className={`sticky top-0 z-50 transition-all duration-300 ${scrolled ? "bg-white/95 shadow-[0_4px_24px_rgba(0,111,185,0.08)] backdrop-blur-md" : "bg-white"}`}>
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-3.5">
          <Link to="/" className="flex items-center gap-2.5" data-testid="logo-link">
            <span className="grid h-11 w-11 place-items-center rounded-xl bg-[#006FB9]">
              <ShieldCheck className="h-6 w-6 text-[#B9DFF3]" />
            </span>
            <span className="leading-tight">
              <span className="block font-heading text-[19px] font-extrabold tracking-tight text-[#0B2748]">ABHI</span>
              <span className="block text-[11px] font-semibold uppercase tracking-[0.22em] text-[#2478A8]">Insurance Agency</span>
            </span>
          </Link>

          <nav className="hidden items-center gap-1 lg:flex">
            {NAV_LINKS.map((link) =>
              link.dropdown ? (
                <div key={link.to} className="group relative">
                  <NavLink
                    to={link.to}
                    data-testid="nav-services"
                    className={({ isActive }) =>
                      `nav-link flex items-center gap-1 ${isActive || location.pathname.startsWith("/services") ? "text-[#006FB9] after:scale-x-100" : ""}`
                    }
                  >
                    {link.label}
                    <ChevronDown className="h-3.5 w-3.5 transition-transform duration-300 group-hover:rotate-180" />
                  </NavLink>
                  <div className="invisible absolute left-0 top-full z-50 w-72 translate-y-2 rounded-2xl border border-[#E1ECF3] bg-white p-2 opacity-0 shadow-[0_20px_50px_rgba(0,111,185,0.15)] transition-all duration-300 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100">
                    {SERVICES.map((s) => (
                      <Link key={s.slug} to={`/services/${s.slug}`} className="block rounded-xl px-4 py-2.5 text-sm font-medium text-[#294A65] transition-colors duration-200 hover:bg-[#EEF6FB] hover:text-[#006FB9]">
                        {s.title}
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <NavLink
                  key={link.to}
                  to={link.to}
                  end={link.to === "/"}
                  data-testid={`nav-${link.label.toLowerCase().replace(/\s/g, "-")}`}
                  className={({ isActive }) => `nav-link ${isActive ? "text-[#006FB9] after:scale-x-100" : ""}`}
                >
                  {link.label}
                </NavLink>
              )
            )}
          </nav>

          <div className="flex items-center gap-3">
            <button onClick={() => openQuote()} className="btn-primary hidden lg:inline-flex" data-testid="header-get-quote">
              Get Quote <ShieldCheck className="h-4 w-4" />
            </button>
            <button onClick={() => setMobileOpen(!mobileOpen)} className="grid h-10 w-10 place-items-center rounded-xl border border-[#DCEAF2] text-[#006FB9] lg:hidden" aria-label="Menu" data-testid="mobile-menu-toggle">
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        <div className={`overflow-hidden bg-white transition-all duration-400 lg:hidden ${mobileOpen ? "max-h-[560px] border-t border-[#E1ECF3]" : "max-h-0"}`}>
          <nav className="space-y-1 px-6 py-4">
            {NAV_LINKS.map((link) =>
              link.dropdown ? (
                <div key={link.to}>
                  <button onClick={() => setMobileServicesOpen(!mobileServicesOpen)} className="flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-[15px] font-semibold text-[#193B58]">
                    Services <ChevronDown className={`h-4 w-4 transition-transform ${mobileServicesOpen ? "rotate-180" : ""}`} />
                  </button>
                  <div className={`overflow-hidden transition-all duration-300 ${mobileServicesOpen ? "max-h-72" : "max-h-0"}`}>
                    {SERVICES.map((s) => (
                      <Link key={s.slug} to={`/services/${s.slug}`} className="block rounded-lg px-6 py-2 text-sm text-[#45647C]">
                        {s.title}
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <NavLink key={link.to} to={link.to} end={link.to === "/"} className="block rounded-xl px-3 py-2.5 text-[15px] font-semibold text-[#193B58]">
                  {link.label}
                </NavLink>
              )
            )}
            <button onClick={() => openQuote()} className="btn-primary mt-2 w-full justify-center" data-testid="mobile-get-quote">
              Get Quote <ShieldCheck className="h-4 w-4" />
            </button>
          </nav>
        </div>
      </header>
    </>
  );
}
